/*
 * Lemonade Training Oy copy rights all right reserved.
 * Smart Workout Parser — pure helper for personalizing percentage-based
 * prescriptions to athlete-specific weights using their 1RM data.
 *
 * Runs client-side on mobile and web. No Firestore, no auth, no network.
 * See LT-302 for the locked architectural decision.
 */

import { OneRepMax } from "./oneRepMax";

// ====================================================================================================
// Public Types
// ====================================================================================================

/**
 * A single percentage-based prescription within a workout.
 *
 * @property movementID - A MovementRegistry entry ID, matched against
 *   `OneRepMax.movementRegistryID`.
 * @property percentage - Percentage of athlete's 1RM, e.g. `85` = 85%. Values
 *   `<= 0` are treated as "no percentage" and the prescription passes through
 *   unchanged.
 */
export type WorkoutPrescription = {
  movementID: string;
  movementName: string;
  sets: number;
  reps: number | string;
  percentage: number;
};

/**
 * Warning codes attached to a personalized prescription. UI is responsible
 * for rendering each (e.g. `EXCEEDS_1RM` should render with `role="alert"`).
 */
export type WorkoutWarning = "NO_1RM" | "EXCEEDS_1RM" | "STALE_1RM";

/**
 * The personalized output for a single prescription. `personalized.weight` is
 * `null` when no 1RM is available, when the movement is non-weight-based, or
 * when `percentage <= 0`.
 */
export type Personalized = {
  movementID: string;
  movementName: string;
  prescribed: { sets: number; reps: number | string; percentage: number };
  personalized: {
    /** Personalized weight in kg, rounded per `options.rounding`. */
    weight: number | null;
    basedOn1RM: number | null;
    basedOn1RMUpdatedAt: Date | null;
  };
  warnings: WorkoutWarning[];
};

/**
 * Options for `personalizeWorkout`. All fields optional with sensible defaults.
 *
 * @property now - Override "now" for deterministic staleness checks in tests.
 * @property stalenessDays - Days after which a 1RM is flagged as `STALE_1RM`.
 *   Default 180.
 * @property rounding - Round the calculated weight to the nearest multiple of
 *   this value. Default 2.5 (kg). Pass `1` for integer kg, `5` for coarser
 *   plate math, etc.
 */
export type PersonalizeOptions = {
  now?: Date;
  stalenessDays?: number;
  rounding?: number;
};

// ====================================================================================================
// Constants
// ====================================================================================================

const DEFAULT_STALENESS_DAYS = 180;
const DEFAULT_ROUNDING_KG = 2.5;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

// ====================================================================================================
// Internal Helpers
// ====================================================================================================

/** When the effort happened: `performedAt`, else `updatedAt`. */
function effortTime(rm: OneRepMax): Date | null {
  return rm.performedAt ?? rm.updatedAt ?? null;
}

/**
 * Callers pass one state-derived record per movement, so the first record whose
 * `movementRegistryID` matches is the athlete's value for that movement.
 */
function findOneRM(
  prescription: WorkoutPrescription,
  oneRMs: OneRepMax[],
): OneRepMax | null {
  return (
    oneRMs.find((rm) => rm.movementRegistryID === prescription.movementID) ??
    null
  );
}

function roundToNearest(value: number, granularity: number): number {
  return Math.round(value / granularity) * granularity;
}

function personalizeOne(
  prescription: WorkoutPrescription,
  oneRMs: OneRepMax[],
  opts: Required<PersonalizeOptions>,
): Personalized {
  const base: Personalized = {
    movementID: prescription.movementID,
    movementName: prescription.movementName,
    prescribed: {
      sets: prescription.sets,
      reps: prescription.reps,
      percentage: prescription.percentage,
    },
    personalized: {
      weight: null,
      basedOn1RM: null,
      basedOn1RMUpdatedAt: null,
    },
    warnings: [],
  };

  if (prescription.percentage <= 0) {
    return base;
  }

  const rm = findOneRM(prescription, oneRMs);

  // Legacy records may lack a numeric value (only `_1RM`); never compute NaN kg.
  if (!rm || typeof rm.value !== "number" || !(rm.value > 0)) {
    return { ...base, warnings: ["NO_1RM"] };
  }

  // Non-weight movement (cal/time/meter): expose 1RM context but no calculated weight.
  if (rm.measureType !== "kg") {
    return {
      ...base,
      personalized: {
        weight: null,
        basedOn1RM: rm.value,
        basedOn1RMUpdatedAt: rm.updatedAt ?? null,
      },
    };
  }

  const rawWeight = rm.value * (prescription.percentage / 100);
  const weight = roundToNearest(rawWeight, opts.rounding);

  const warnings: WorkoutWarning[] = [];
  if (rawWeight > rm.value) {
    warnings.push("EXCEEDS_1RM");
  }
  const performed = effortTime(rm);
  if (performed) {
    const ageDays = (opts.now.getTime() - performed.getTime()) / MS_PER_DAY;
    if (ageDays > opts.stalenessDays) {
      warnings.push("STALE_1RM");
    }
  }

  return {
    ...base,
    personalized: {
      weight,
      basedOn1RM: rm.value,
      basedOn1RMUpdatedAt: rm.updatedAt ?? null,
    },
    warnings,
  };
}

// ====================================================================================================
// Public API
// ====================================================================================================

/**
 * Convert a list of percentage-based workout prescriptions to athlete-specific
 * personalized weights, with warnings for missing 1RMs, over-1RM calculations,
 * and stale 1RM data.
 *
 * Pure function: no Firestore, no auth, no network. Safe to call on every
 * render. Performance budget: < 5 ms for a 5-movement workout against 100
 * 1RM records.
 *
 * @example
 *   import { personalizeWorkout } from "@lemonadetraining/types";
 *   const result = personalizeWorkout(workout.prescriptions, athlete1RMs);
 *   // → [{ personalized: { weight: 102.5, ... }, warnings: [] }, ...]
 */
export function personalizeWorkout(
  prescriptions: WorkoutPrescription[],
  oneRMs: OneRepMax[],
  options?: PersonalizeOptions,
): Personalized[] {
  const opts: Required<PersonalizeOptions> = {
    now: options?.now ?? new Date(),
    stalenessDays: options?.stalenessDays ?? DEFAULT_STALENESS_DAYS,
    rounding: options?.rounding ?? DEFAULT_ROUNDING_KG,
  };
  return prescriptions.map((p) => personalizeOne(p, oneRMs, opts));
}
